import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";

const FAQS = [
  {
    q: "What exactly does Thom Kriner LLC do?",
    a: "Put simply, we help growing businesses fix broken workflows and align the teams running them. When a major project stalls, or a new piece of software isn't working the way you hoped, the problem is usually a mix of confusing processes and frustrated employees. We step in, find the exact bottleneck, redesign the workflow to be efficient, and make sure your team is fully equipped to execute it.",
  },
  {
    q: "You mention \u201cHuman-in-the-Loop\u201d (HITL) architecture. What does that mean for my business?",
    a: "Many businesses are buying AI tools or automation to save time. That technology is great for standard, everyday tasks. But when the software hits a problem it wasn't programmed for (an \u201cexception\u201d), it breaks down and leaves your employees to clean up the mess. \u201cHuman-in-the-Loop\u201d simply means we design a clear backup plan. We build the exact steps your employees need to take when the technology fails, ensuring your business keeps running smoothly without frustrating your team or your customers.",
  },
  {
    q: "We just spent a lot of money on new enterprise software, but my team refuses to use it correctly. Can you help?",
    a: "Yes. Traditional consultants will tell you to send your employees to another training class. We take a different approach. We build step-by-step, interactive guidance directly into the software itself (using tools like WalkMe). Instead of expecting your team to memorize a massive training manual, the software guides them in real-time as they work. This drastically reduces confusion, eliminates errors, and guarantees your team actually uses the tools you paid for.",
  },
  {
    q: "My leadership team is constantly arguing and it is slowing down our operations. How do you fix that without it feeling like corporate therapy?",
    a: "We don't treat team friction as a personality problem; we treat it as a data problem. We use objective, proven behavioral assessments to figure out exactly why communication is breaking down. Often, leaders are just clashing over how they process information or what they need from each other to feel respected. We take that data and build concrete \u201cRules of Engagement\u201d for your team, removing the ego from the room so your leaders can stop arguing and start executing.",
  },
  {
    q: "We've hired consultants before who handed us a report and left. How do we know your changes will actually stick?",
    a: "We don't just map out a new process and walk away. A core part of our service is the \u201cControl Phase.\u201d We work with you to set up simple performance dashboards and tracking tools. This gives your leadership team real-time visibility to ensure the new workflows are actually saving you time and money long after our project is complete.",
  },
  {
    q: "Do you only work with massive corporations?",
    a: "Not at all. While our founder spent years engineering processes for massive enterprise healthcare and global brands like Kinko's/FedEx, our passion is bringing that same elite architecture to mid-size businesses. Growing companies are rapidly adopting powerful tools like AI and complex software to scale up; our job is to make sure your operational processes and your people are strong enough to support that growth.",
  },
];

export default function FAQ() {
  const [active, setActive] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-4xl px-6 lg:px-12">
        <SectionHeader
          index="07"
          label="FAQ"
          testid="faq-heading"
          title={
            <>
              Questions, <span className="italic text-[#EB5E28]">answered</span>.
            </>
          }
        />

        <div className="mt-12">
          {FAQS.map((f, i) => {
            const isActive = active === i;
            return (
              <Reveal key={f.q} delay={0.04 * i}>
                <div
                  data-testid={`faq-item-${i + 1}`}
                  className={`border-b border-[#E2E8F0] transition-colors duration-300 ${
                    isActive ? "border-[#EB5E28]/40" : ""
                  }`}
                >
                  <button
                    data-testid={`faq-toggle-${i + 1}`}
                    onClick={() => setActive(isActive ? -1 : i)}
                    className="flex w-full items-center justify-between gap-6 py-6 text-left"
                  >
                    <h3
                      className={`font-display text-lg tracking-tight transition-colors duration-300 sm:text-xl ${
                        isActive ? "font-semibold text-[#0F141A]" : "font-medium text-[#4A5361] hover:text-[#0F141A]"
                      }`}
                    >
                      {f.q}
                    </h3>
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                        isActive
                          ? "rotate-45 border-[#EB5E28]/50 text-[#EB5E28]"
                          : "border-[#CBD5E1] text-[#626D7D]"
                      }`}
                    >
                      <Plus className="h-4 w-4" />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        key="answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-7 pr-8 text-sm leading-relaxed text-[#4A5361] sm:text-base">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
